import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { ArrowRight, Map, BookOpen, Sparkles, Award, Quote } from "lucide-react";
import api from "../lib/api";
import { PricingSection } from "../components/PricingSection";
import { TESTIMONIALS } from "./TestimonialsPage";

export default function Home() {
  const { t } = useTranslation();
  const [levels, setLevels] = useState([]);

  useEffect(() => {
    api.get("/levels").then(({ data }) => setLevels(data)).catch(() => {});
  }, []);

  return (
    <main className="relative z-10">
      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 flex justify-end">
          <div className="relative w-full md:w-[62%] h-full">
            <img
              src="/lakou/lakou4.jpg"
              alt="Le péristyle du Lakou Imperial Savann Karé, avec le buste de Dutty Boukman"
              className="w-full h-full object-cover object-center" />
            <div className="absolute inset-0 bg-gradient-to-r from-background via-background/45 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
          </div>
        </div>
        <img src="/veve-legba-blue.png" alt="" aria-hidden="true"
          className="absolute -left-16 top-1/2 -translate-y-1/2 w-[440px] opacity-[0.14] hidden lg:block pointer-events-none select-none"
          data-testid="hero-veve-legba" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 w-full">
          <div className="max-w-2xl fade-up">
            <span className="inline-block text-xs uppercase tracking-[0.3em] text-primary border border-primary/30 rounded-full px-4 py-1.5 mb-8" data-testid="hero-badge">
              {t("hero.badge")}
            </span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-serif-display font-medium tracking-tight text-foreground leading-[1.05]">
              {t("hero.title")}
            </h1>
            <p className="font-serif-display italic text-2xl text-primary mt-3">{t("hero.subtitle")}</p>
            <p className="text-base lg:text-lg text-foreground/75 mt-8 leading-relaxed max-w-xl">{t("hero.text")}</p>
            <p className="text-sm text-muted-foreground mt-4">{t("hero.direction")}</p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/parcours" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:bg-[#16528A] transition-colors duration-200" data-testid="hero-cta-parcours">
                {t("hero.cta")} <ArrowRight size={16} />
              </Link>
              <Link to="/carte" className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-primary/40 text-primary hover:bg-primary/10 transition-colors duration-200" data-testid="hero-cta-carte">
                <Map size={16} /> {t("hero.cta2")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Levels */}
      <section className="max-w-7xl mx-auto px-6 py-24 veve-bg" data-testid="levels-section">
        <div className="gold-line mb-16" />
        <h2 className="text-3xl sm:text-4xl font-serif-display tracking-tight mb-12">{t("levels.title")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {levels.map((lvl, i) => (
            <div key={lvl.level_id}
              className={`card-lift bg-card border border-primary/15 rounded-lg p-8 flex flex-col ${i === 0 ? "md:-mt-4" : ""}`}
              data-testid={`level-card-${lvl.level_id}`}>
              <div className="flex items-center justify-between mb-6">
                <span className="font-serif-display text-5xl text-primary/30">{String(lvl.level_id).padStart(2, "0")}</span>
                <span className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border ${lvl.status === "available" ? "border-primary/40 text-primary" : "border-muted-foreground/30 text-muted-foreground"}`}>
                  {lvl.status === "available" ? t("levels.available") : t("levels.coming")}
                </span>
              </div>
              <h3 className="text-2xl font-serif-display text-foreground">{lvl.name}</h3>
              <p className="text-xs text-primary/80 mt-1 uppercase tracking-wider">{lvl.subtitle}</p>
              <p className="text-sm text-foreground/70 mt-4 leading-relaxed flex-1">{lvl.description}</p>
              {lvl.status === "available" && (
                <Link to="/parcours" className="inline-flex items-center gap-2 text-sm text-primary mt-6 hover:gap-3 transition-[gap] duration-200" data-testid={`level-explore-${lvl.level_id}`}>
                  {t("levels.explore")} <ArrowRight size={14} />
                </Link>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Features strip */}
      <section className="border-y border-primary/10 bg-card/50">
        <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
          {[
            { icon: BookOpen, label: "6 cours · ~21h", sub: "Niveau 1 Découverte" },
            { icon: Sparkles, label: "Assistant IA", sub: "FR · EN · Kreyòl" },
            { icon: Map, label: "Carte interactive", sub: "11 traditions" },
            { icon: Award, label: "Certificats", sub: "Vérifiables par QR code" },
          ].map((f) => (
            <div key={f.label} className="flex items-start gap-4">
              <f.icon size={22} strokeWidth={1.5} className="text-primary mt-1" />
              <div>
                <p className="text-sm font-medium text-foreground">{f.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{f.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Le Lakou */}
      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="lakou-section">
        <div className="gold-line mb-16" />
        <p className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Notre ancrage</p>
        <h2 className="text-3xl sm:text-4xl font-serif-display tracking-tight mb-4">Le Lakou Imperial Savann Karé</h2>
        <p className="text-foreground/70 max-w-2xl leading-relaxed mb-10">
          Le cœur vivant de la transmission : un lieu de mémoire, de cérémonie et d'enseignement où le parcours Chimen Nanm prend racine, sous le regard de Dutty Boukman.
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[4, 1, 2, 3].map((n, i) => (
            <div key={n} className={`card-lift overflow-hidden rounded-lg border border-primary/15 ${i === 0 ? "col-span-2 row-span-2" : ""}`} data-testid={`lakou-photo-${n}`}>
              <img src={`/lakou/lakou${n}.jpg`} alt={`Lakou Imperial Savann Karé — vue ${n}`} className="w-full h-full object-cover aspect-[4/3]" loading="lazy" />
            </div>
          ))}
        </div>
      </section>

      {/* Témoignages */}
      <section className="border-y border-primary/10 bg-card/50" data-testid="home-testimonials-section">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <p className="text-xs uppercase tracking-[0.3em] text-primary mb-3">{t("testimonials.badge")}</p>
          <h2 className="text-3xl sm:text-4xl font-serif-display tracking-tight mb-12">{t("testimonials.title")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.slice(0, 3).map((tm) => (
              <figure key={tm.id} className="bg-background border border-primary/15 rounded-lg p-7 flex flex-col" data-testid={`home-testimonial-${tm.id}`}>
                <Quote size={20} strokeWidth={1.5} className="text-primary/50" />
                <blockquote className="text-sm text-foreground/80 leading-relaxed mt-4 flex-1 line-clamp-6">
                  « {tm.text} »
                </blockquote>
                <figcaption className="mt-5 pt-4 border-t border-primary/10">
                  <p className="font-medium text-sm">{tm.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{tm.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
          <Link to="/temoignages" className="inline-flex items-center gap-2 text-sm text-primary mt-10 hover:gap-3 transition-[gap] duration-200" data-testid="home-testimonials-more">
            {t("testimonials.seeAll")} <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <PricingSection />
    </main>
  );
}
